import React, { useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import InclusionAssetEditFields from './InclusionAssetEditFields';

export interface InclusionAssetEditValue {
  category: string;
  categoryId?: string | null;
  currency?: string;
  isHidden: boolean;
  name: string;
  requiresBooking?: boolean;
  rrp?: number | null;
  type?: string;
  unit?: string;
}

interface Props {
  unassignActionLabel?: string;
  unassignConfirmationText?: string;
  isCustom?: boolean;
  isSaving?: boolean;
  open: boolean;
  value: InclusionAssetEditValue;
  onChange: (nextValue: InclusionAssetEditValue) => void;
  onClose: () => void;
  onUnassign?: () => Promise<void>;
  onSave: () => void;
}

export default function InclusionAssetEditDialog({
  unassignActionLabel = 'Unassign',
  unassignConfirmationText,
  isCustom,
  isSaving,
  open,
  value,
  onChange,
  onClose,
  onUnassign,
  onSave,
}: Props) {
  const [isUnassignDialogOpen, setIsUnassignDialogOpen] = useState(false);
  const canSave = !!value.name.trim() && !!value.category.trim() && !!value.unit?.trim();
  const handleUnassign = async () => {
    if (!onUnassign) {
      return;
    }

    try {
      await onUnassign();
      setIsUnassignDialogOpen(false);
    } catch {
      // Error feedback is handled by callers.
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Edit Inclusion Asset</DialogTitle>
        <DialogContent dividers>
          <InclusionAssetEditFields isCustom={isCustom} open={open} value={value} onChange={onChange} />
        </DialogContent>
        <DialogActions>
          {onUnassign && (
            <Button
              variant="text"
              color="error"
              disabled={isSaving}
              onClick={() => setIsUnassignDialogOpen(true)}
              sx={{ mr: 'auto' }}
            >
              {unassignActionLabel}
            </Button>
          )}
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSave} disabled={isSaving || !canSave}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isUnassignDialogOpen} onClose={() => setIsUnassignDialogOpen(false)}>
        <DialogTitle>Unassign inclusion</DialogTitle>
        <DialogContent>
          <Typography>{unassignConfirmationText ?? `Unassign ${value.name || 'this inclusion'}?`}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" disabled={isSaving} onClick={() => setIsUnassignDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" disabled={isSaving} onClick={() => void handleUnassign()}>
            {isSaving ? 'Unassigning...' : unassignActionLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
